import type { TooltipProps } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import { fetchPrepItemDetail, type PrepDetailResponse, type PrepListItem } from '../../../api/prep'
import { getChipColors } from '../../../utils/colorByCategory'

export const PREP_FETCH_RETRY_DELAYS_MS = [400, 1200, 2500, 5000]

// Fatal HTTP statuses where retrying would be wasteful because the server reports a permanent failure
const NON_RETRIABLE_HTTP_STATUSES = new Set([508])

const jitterDelay = (ms: number): number => {
  const jitter = ms * 0.25
  const min = ms - jitter
  const max = ms + jitter
  return Math.max(0, Math.round(Math.random() * (max - min) + min))
}

export const computeRetryDelay = (baseMs: number): number => jitterDelay(baseMs)

export const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    if (ms <= 0) {
      resolve()
      return
    }

    if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
      window.setTimeout(resolve, ms)
      return
    }

    setTimeout(resolve, ms)
  })

export const isRetriablePrepFetchError = (error: unknown, options: { allow508?: boolean } = {}): boolean => {
  const { allow508 = false } = options
  if (!error) {
    return false
  }

  if (error instanceof TypeError) {
    return true
  }

  if (typeof error === 'object') {
    const withStatus = error as { status?: unknown; code?: unknown; name?: unknown; message?: unknown }
    const status = Number(withStatus.status)
    if (Number.isFinite(status)) {
      if (status === 508 && allow508) {
        return true
      }

      if (NON_RETRIABLE_HTTP_STATUSES.has(status)) {
        return false
      }

      return status >= 500
    }

    const code = typeof withStatus.code === 'string' ? withStatus.code.toLowerCase() : ''
    if (code.includes('network')) {
      return true
    }

    const name = typeof withStatus.name === 'string' ? withStatus.name.toLowerCase() : ''
    if (name === 'networkerror') {
      return true
    }

    const message = typeof withStatus.message === 'string' ? withStatus.message.toLowerCase() : ''
    if (message.includes('network error') || message.includes('failed to fetch')) {
      return true
    }
  }

  return false
}

const CHIP_BASE_SX: SxProps<Theme> = {
  borderRadius: 999,
  px: 1.25,
  py: 0.25,
  minHeight: 28,
  fontWeight: 600,
  flexShrink: 1,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '& .MuiChip-label': {
    px: 0.5,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}

export const buildCategoryChipSx = (slug: string | null): SxProps<Theme> => {
  const normalizedSlug = slug?.trim().toLowerCase() ?? 'prep'
  const chipColors = getChipColors(normalizedSlug || 'prep', { selected: false })

  return {
    ...CHIP_BASE_SX,
    backgroundColor: chipColors.background,
    color: chipColors.text,
    border: (theme) => `1px solid ${theme.palette.common.white}`,
  }
}

export const buildNeutralChipSx = (): SxProps<Theme> => ({
  ...CHIP_BASE_SX,
  backgroundColor: (theme) => theme.palette.action.hover,
  color: (theme) => theme.palette.text.primary,
  border: (theme) => `1px solid ${theme.palette.common.white}`,
})

export const extractRequestId = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const withRequest = error as { request_id?: unknown; requestId?: unknown; data?: unknown }

  if (typeof withRequest.request_id === 'string') {
    return withRequest.request_id
  }
  if (typeof withRequest.requestId === 'string') {
    return withRequest.requestId
  }

  if (withRequest.data && typeof withRequest.data === 'object') {
    const data = withRequest.data as { request_id?: unknown; requestId?: unknown }
    if (typeof data.request_id === 'string') {
      return data.request_id
    }
    if (typeof data.requestId === 'string') {
      return data.requestId
    }
  }

  return undefined
}

export const withRequestId = (message: string, requestId?: string): string => {
  if (!requestId) {
    return message
  }
  return `${message} (Request ID: ${requestId})`
}

export const createItemTooltipSlotProps = (): TooltipProps['slotProps'] => ({
  tooltip: {
    sx: (theme) => ({
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.grey[800]
          : theme.palette.grey[900],
      color: theme.palette.common.white,
      opacity: 1,
      px: 1.5,
      py: 1.25,
      boxShadow: theme.shadows[6],
    }),
  },
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [40, 0],
        },
      },
    ],
  },
})

const hasNumericId = (value: unknown): value is { id: number } => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const withId = value as { id?: unknown }
  return typeof withId.id === 'number' && Number.isFinite(withId.id)
}

const looksLikePrepListItem = (value: unknown): value is PrepListItem => {
  if (!hasNumericId(value)) {
    return false
  }

  const candidate = value as Record<string, unknown>

  if ('error' in candidate) {
    return false
  }

  return (
    'headline' in candidate ||
    'tease' in candidate ||
    'short_preview' in candidate ||
    'short_story_present' in candidate ||
    'categories' in candidate ||
    'primary_category' in candidate ||
    'published_at' in candidate
  )
}

const extractPlaceholderId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (hasNumericId(value)) {
    return value.id
  }

  return null
}

const extractPlaceholderText = (value: unknown): string | null => {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  if (typeof value !== 'object') {
    return null
  }

  const record = value as Record<string, unknown>
  const directKeys = [
    'placeholder_text',
    'placeholderText',
    'slot_text',
    'slotText',
    'text',
    'message',
    'detail',
    'description',
  ] as const

  for (const key of directKeys) {
    const candidate = record[key]
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }

  const nestedKeys = ['placeholder', 'slot', 'error', 'data'] as const

  for (const key of nestedKeys) {
    if (key in record) {
      const nested = extractPlaceholderText(record[key])
      if (nested) {
        return nested
      }
    }
  }

  return null
}

export type PrepDashboardNormalizedEntry =
  | { kind: 'item'; item: PrepListItem }
  | { kind: 'placeholder'; id: number; placeholderText?: string | null; error?: unknown }

export const normalizePrepDashboardItems = (
  payload: unknown,
): PrepDashboardNormalizedEntry[] => {
  if (!Array.isArray(payload)) {
    return []
  }

  return payload
    .map<PrepDashboardNormalizedEntry | null>((entry) => {
      if (looksLikePrepListItem(entry)) {
        return { kind: 'item', item: entry }
      }

      const id = extractPlaceholderId(entry)
      if (id === null) {
        return null
      }

      const errorDetail =
        entry && typeof entry === 'object' && 'error' in (entry as Record<string, unknown>)
          ? (entry as { error?: unknown }).error
          : undefined
      const placeholderText = extractPlaceholderText(entry)

      return { kind: 'placeholder', id, error: errorDetail, placeholderText }
    })
    .filter((entry): entry is PrepDashboardNormalizedEntry => entry !== null)
}

export interface FetchPrepItemDetailWithRetryOptions {
  signal?: AbortSignal
}

const resolveAbortReason = (signal: AbortSignal | undefined): unknown => {
  if (!signal || !signal.aborted) {
    return undefined
  }

  const reason = (signal as { reason?: unknown }).reason
  if (reason !== undefined) {
    return reason
  }

  return new Error('Aborted')
}

export const fetchPrepItemDetailWithRetry = async (
  id: number,
  options: FetchPrepItemDetailWithRetryOptions = {},
): Promise<PrepDetailResponse> => {
  const { signal } = options
  const attemptCount = PREP_FETCH_RETRY_DELAYS_MS.length + 1
  let lastError: unknown

  for (let attemptIndex = 0; attemptIndex < attemptCount; attemptIndex += 1) {
    if (signal?.aborted) {
      throw resolveAbortReason(signal)
    }

    const controller = new AbortController()
    const abortListener = () => {
      controller.abort()
    }

    signal?.addEventListener('abort', abortListener)

    try {
      const detail = await fetchPrepItemDetail(id, { signal: controller.signal })
      return detail
    } catch (error) {
      if (controller.signal.aborted) {
        if (signal?.aborted) {
          throw resolveAbortReason(signal)
        }
        throw error
      }

      lastError = error
      const hasMoreAttempts = attemptIndex < attemptCount - 1
      if (!hasMoreAttempts || !isRetriablePrepFetchError(error, { allow508: true })) {
        throw error
      }

      const delayMs = computeRetryDelay(PREP_FETCH_RETRY_DELAYS_MS[attemptIndex] ?? 0)
      if (delayMs > 0) {
        await wait(delayMs)
      }
    } finally {
      signal?.removeEventListener('abort', abortListener)
    }
  }

  throw lastError ?? new Error('Unable to load prep item detail.')
}
